<script lang="ts">
  import { LayoutDashboard, Timer, ListChecks, Users, ChartPie, Search, Plus, PanelLeftClose, PanelLeftOpen, Settings, LifeBuoy, LogOut, Sun, Moon, Monitor, Keyboard, ChevronsUpDown } from '@lucide/svelte';
  import Logo from './Logo.svelte';
  import TimerMini from './TimerMini.svelte';
  import Kbd from '../ui/Kbd.svelte';
  import Menu, { type MenuItem } from '../ui/Menu.svelte';
  import Avatar from '../ui/Avatar.svelte';
  import ColorDot from '../ui/ColorDot.svelte';
  import { router } from '../router.svelte';
  import { prefs } from '../prefs.svelte';
  import { session } from '../stores/session.svelte';
  import { data } from '../stores/data.svelte';
  import { t } from '../i18n.svelte';
  import { clientColor } from '../format';
  import { tooltip } from '../ui/actions';
  import { shell } from './shell.svelte';

  const collapsed = $derived(prefs.value.sidebarCollapsed);
  const brandStyle = $derived(prefs.value.sidebarStyle === 'brand');
  const nav = $derived([
    { href: '/app', label: t('nav.dashboard'), icon: LayoutDashboard, exact: true },
    { href: '/app/timer', label: t('nav.timer'), icon: Timer },
    { href: '/app/registraties', label: t('nav.entries'), icon: ListChecks },
    { href: '/app/klanten', label: t('nav.clients'), icon: Users },
    { href: '/app/rapporten', label: t('nav.reports'), icon: ChartPie }
  ]);

  // Clients with the most recent activity first.
  const recentClients = $derived.by(() => {
    void data.version;
    const last = new Map<number, string>();
    for (const e of data.entries) {
      if (e.client_id && !last.has(e.client_id)) last.set(e.client_id, e.start_time);
    }
    return [...data.activeClients]
      .sort((a, b) => (last.get(b.id) || '').localeCompare(last.get(a.id) || '') || a.name.localeCompare(b.name))
      .slice(0, 7);
  });

  const userMenu = $derived<MenuItem[]>([
    { label: session.user?.name || '', heading: true },
    { label: t('nav.settings'), icon: Settings, href: '/app/instellingen' },
    { label: t('nav.support'), icon: LifeBuoy, href: '/app/support' },
    { label: t('shell.shortcuts'), icon: Keyboard, onclick: () => (shell.shortcutsOpen = true) },
    { divider: true },
    { label: t('shell.theme'), heading: true },
    { label: t('shell.theme_light'), icon: Sun, checked: prefs.value.theme === 'light', onclick: () => prefs.update({ theme: 'light' }) },
    { label: t('shell.theme_dark'), icon: Moon, checked: prefs.value.theme === 'dark', onclick: () => prefs.update({ theme: 'dark' }) },
    { label: t('shell.theme_system'), icon: Monitor, checked: prefs.value.theme === 'system', onclick: () => prefs.update({ theme: 'system' }) },
    { divider: true },
    { label: t('nav.logout'), icon: LogOut, danger: true, onclick: () => session.logout() }
  ]);
</script>

<aside class="sidebar" class:collapsed>
  <div class="top">
    <a href="/app" class="brand" aria-label="Timepeak">
      {#if collapsed}<Logo wordmark={false} size={28} />{:else}<Logo size={28} inverse={brandStyle} />{/if}
    </a>
    <button type="button" class="collapse" aria-label={collapsed ? t('nav.expand') : t('nav.collapse')} use:tooltip={collapsed ? t('nav.expand') : t('nav.collapse')} onclick={() => prefs.update({ sidebarCollapsed: !collapsed })}>
      {#if collapsed}<PanelLeftOpen size={16} />{:else}<PanelLeftClose size={16} />{/if}
    </button>
  </div>

  <button type="button" class="search" onclick={() => (shell.paletteOpen = true)} aria-label={t('nav.search')} use:tooltip={collapsed ? t('nav.search') : null}>
    <Search size={15} strokeWidth={2} />
    {#if !collapsed}<span class="search-text">{t('common.search')}</span><Kbd tone={brandStyle ? 'inherit' : 'default'}>{navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'} K</Kbd>{/if}
  </button>

  <nav class="nav" aria-label={t('nav.menu')}>
    {#each nav as item (item.href)}
      {@const active = router.isActive(item.href, item.exact)}
      <a href={item.href} class="item" class:active aria-current={active ? 'page' : undefined} use:tooltip={collapsed ? item.label : null}>
        <span class="ic"><item.icon size={17} strokeWidth={active ? 2 : 1.75} /></span>
        {#if !collapsed}<span class="truncate">{item.label}</span>{/if}
      </a>
    {/each}
  </nav>

  {#if prefs.value.showClientsInSidebar && !collapsed}
    <div class="section">
      <div class="section-head">
        <span class="label">{t('nav.clients')}</span>
        <a href="/app/klanten?new=1" class="add" use:tooltip={t('nav.new_client')} aria-label={t('nav.new_client')}><Plus size={14} /></a>
      </div>
      <div class="clients">
        {#each recentClients as client (client.id)}
          {@const active = router.path === `/app/klanten/${client.id}`}
          <a href="/app/klanten/{client.id}" class="client" class:active aria-current={active ? 'page' : undefined}>
            <span class="dot"><ColorDot color={clientColor(client)} size="xs" /></span>
            <span class="truncate">{client.name}</span>
          </a>
        {:else}
          <span class="none">{t('shell.no_clients')}</span>
        {/each}
        {#if data.activeClients.length > recentClients.length}
          <a href="/app/klanten" class="client more">
            <span class="truncate">{t('shell.clients_all')}</span>
            <span class="count tabular">{data.activeClients.length}</span>
          </a>
        {/if}
      </div>
    </div>
  {/if}

  <div class="bottom">
    <TimerMini {collapsed} />
    <Menu items={userMenu} placement={collapsed ? 'right-start' : 'top-start'} width="240px">
      {#snippet trigger({ toggle, open })}
        <button type="button" class="user" class:open onclick={toggle} aria-label={t('nav.profile')}>
          <Avatar name={session.user?.name || session.user?.email} size={28} />
          {#if !collapsed}
            <span class="who">
              <span class="uname truncate">{session.user?.name || '—'}</span>
              <span class="mail truncate">{session.user?.email || ''}</span>
            </span>
            <span class="chev"><ChevronsUpDown size={14} /></span>
          {/if}
        </button>
      {/snippet}
    </Menu>
  </div>
</aside>

<style>
  /* Colours by role, so the brand-coloured sidebar only swaps these. */
  .sidebar {
    --sb-muted: var(--text-3);
    --sb-icon: var(--text-3);
    --sb-strong: var(--text);
    --sb-hover: color-mix(in srgb, var(--text) 5%, transparent);
    /* The current page is ink on a soft tint: blue is kept for actions and data. */
    --sb-active-bg: color-mix(in srgb, var(--text) 7%, transparent);
    --sb-active: var(--text);
    --sb-field-bg: color-mix(in srgb, var(--text) 4%, transparent);
    --sb-field-text: var(--text-3);
    --sb-field-hover-bg: color-mix(in srgb, var(--text) 7%, transparent);
    --sb-field-hover-text: var(--text-2);
    --sb-line: var(--border);
  }
  :global([data-sidebar='brand']) .sidebar {
    --sb-muted: var(--on-brand-3);
    --sb-icon: var(--on-brand-2);
    --sb-strong: var(--on-brand);
    --sb-hover: var(--on-brand-hover);
    --sb-active-bg: var(--on-brand-line);
    --sb-active: var(--on-brand);
    --sb-field-bg: var(--on-brand-hover);
    --sb-field-text: var(--on-brand-2);
    --sb-field-hover-bg: var(--on-brand-line);
    --sb-field-hover-text: var(--on-brand);
    --sb-line: var(--on-brand-line);
  }

  .sidebar {
    position: sticky; top: 0;
    height: 100vh; height: 100dvh;
    width: var(--sidebar-w);
    flex-shrink: 0;
    display: flex; flex-direction: column;
    padding: var(--space-3) var(--space-3) var(--space-2_5);
    background: var(--sidebar-bg);
    border-right: 1px solid var(--sidebar-border);
    color: var(--sidebar-text);
    transition: width var(--dur-slow) var(--ease);
    overflow: hidden;
  }
  .collapsed { width: var(--sidebar-w-collapsed); padding-inline: var(--space-2_5); }

  /* Brand row; the collapse toggle only shows when the pointer is in the sidebar. */
  .top { display: flex; align-items: center; justify-content: space-between; gap: var(--space-1_5); height: 40px; padding: 0 var(--space-0_5) 0 var(--space-2); margin-bottom: var(--space-3); }
  .collapsed .top { flex-direction: column; height: auto; gap: var(--space-2); padding: var(--space-1) 0 0; }
  .brand { display: inline-flex; text-decoration: none; }
  .collapse, .add { border: 0; border-radius: var(--radius-xs); background: transparent; color: var(--sb-muted); display: grid; place-items: center; transition: background var(--dur), color var(--dur), opacity var(--dur); }
  .collapse { width: 28px; height: 28px; }
  .add { width: 22px; height: 22px; }
  .collapse:hover, .add:hover { background: var(--sb-hover); color: var(--sb-strong); }
  @media (hover: hover) {
    .sidebar:not(.collapsed):not(:hover):not(:focus-within) .collapse { opacity: 0; }
  }

  .search {
    display: flex; align-items: center; gap: var(--space-2);
    height: 34px; padding: 0 var(--space-1_5) 0 var(--space-2_5);
    border: 0; border-radius: var(--radius-sm);
    background: var(--sb-field-bg); color: var(--sb-field-text);
    font-size: var(--text-base); text-align: left;
    margin-bottom: var(--space-4);
    transition: background var(--dur), color var(--dur);
  }
  .collapsed .search { justify-content: center; padding: 0; }
  .search:hover { background: var(--sb-field-hover-bg); color: var(--sb-field-hover-text); }
  .search-text { flex: 1; }

  .nav { display: flex; flex-direction: column; gap: 2px; }
  .item, .client { display: flex; align-items: center; border-radius: var(--radius-sm); color: var(--sidebar-text); text-decoration: none; transition: background var(--dur), color var(--dur); }
  .item:hover, .client:hover { background: var(--sb-hover); color: var(--sb-strong); text-decoration: none; }
  .item { gap: var(--space-2_5); height: 34px; padding: 0 var(--space-2_5); font-size: var(--text-base); font-weight: var(--weight-medium); }
  .collapsed .item { justify-content: center; padding: 0; }
  .ic { display: inline-flex; flex-shrink: 0; color: var(--sb-icon); transition: color var(--dur); }
  .item:hover .ic { color: var(--sb-strong); }
  .item.active { background: var(--sb-active-bg); color: var(--sb-active); }
  .item.active .ic { color: var(--sb-active); }

  .section { margin-top: var(--space-6); display: flex; flex-direction: column; min-height: 0; flex: 1; }
  .section-head { display: flex; align-items: center; justify-content: space-between; height: 24px; padding: 0 var(--space-1) 0 var(--space-2_5); margin-bottom: var(--space-1); }
  .label { color: var(--sb-muted); font-size: var(--text-xs); font-weight: var(--weight-medium); letter-spacing: 0.01em; }
  .clients { display: flex; flex-direction: column; gap: 1px; overflow-y: auto; min-height: 0; scrollbar-width: none; }
  .clients::-webkit-scrollbar { display: none; }
  .client { gap: var(--space-2_5); height: 30px; padding: 0 var(--space-2_5); font-size: var(--text-sm); flex-shrink: 0; }
  /* Same width as a nav icon, so names line up with the labels above. */
  .dot { width: 17px; display: inline-flex; justify-content: center; flex-shrink: 0; }
  /* On the blue sidebar a blue client colour would vanish: give dots a light edge. */
  :global([data-sidebar='brand']) .dot :global(> *) { box-shadow: 0 0 0 1.5px var(--on-brand-2); }
  .client.active { background: var(--sb-active-bg); color: var(--sb-active); font-weight: var(--weight-medium); }
  .client.more { color: var(--sb-muted); justify-content: space-between; padding-left: calc(var(--space-2_5) + 17px + var(--space-2_5)); }
  .client.more:hover { color: var(--sb-strong); }
  .count { font-size: var(--text-xs); }
  .none { padding: var(--space-1) var(--space-2_5); font-size: var(--text-sm); color: var(--sb-muted); }

  .bottom { margin-top: auto; display: flex; flex-direction: column; gap: var(--space-2_5); padding-top: var(--space-3); }
  .user {
    display: flex; align-items: center; gap: var(--space-2_5); width: 100%;
    padding: var(--space-2) var(--space-1_5); border: 0; border-top: 1px solid var(--sb-line); border-radius: 0;
    background: transparent; color: var(--sb-strong); text-align: left;
    transition: background var(--dur);
  }
  .user:hover, .user.open { background: var(--sb-hover); border-radius: var(--radius-sm); border-top-color: transparent; }
  .collapsed .user { justify-content: center; padding-inline: 0; }
  .who { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: var(--leading-tight); gap: 2px; }
  .uname { font-size: var(--text-sm); font-weight: var(--weight-semibold); }
  .mail { font-size: var(--text-xs); color: var(--sb-muted); }
  .chev { display: inline-flex; color: var(--sb-muted); }
</style>
